import ProductionDiffLine from '../ProductionDiffLine'
import { LinearGauge } from 'canvas-gauges'
import { pathOr } from 'ramda'
import { UNIT_TEMPERATURE } from '../Measurement'
import { Footer, useTheme, Status, Button } from '@iqueue/ui-kit'
import React, { useRef, useEffect, useState } from 'react'
import cn from 'classnames'
import { t } from '../../../hooks/useTranslate'
import SensorInfoModal from '../../SensorInfoModal'

export default function TemperatureCard({ title, summary, scope, className, sensors = [], ...props }) {
  const [width, setWidth] = useState(0)
  const { getCSSValue } = useTheme()
  const canvasRefs = useRef([])
  const [currentSensor, setCurrentSensor] = useState({})
  const [isOpened, setIsOpened] = useState(false)
  const gaugeRefs = useRef([])

  const temperatureSensors = summary?.sensors?.filter(sensor => (scope === 'TEMPERATURE' && sensor.type === 'TEMPERATURE')) || []

  useEffect(() => {
    const w = Math.min(300, width)

    temperatureSensors.forEach((sensor, index) => {
      const { limits, thresholds } = sensor
      if (!gaugeRefs.current[index]) {
        gaugeRefs.current[index] = new LinearGauge({
          renderTo: canvasRefs.current[index],
          width: (w / 16) * 9,
          height: w,
          units: "°C",
          title: false,
          minValue: limits?.min || 0,
          maxValue: limits?.max || 100,
          majorTicks: Array.from(
            { length: 6 },
            (_, i) => String(
              Math.round(Number(((limits?.min || 0) + i * ((limits?.max || 100) - (limits?.min || 0)) / 5))),
            ),
          ),

          minorTicks: 6,
          strokeTicks: true,
          highlights: [
            ...(thresholds?.critical || []).map(([from, to]) => ({ from, to, color: 'rgba(255, 0, 0, 0.7)' })),
            ...(thresholds?.warn || []).map(([from, to]) => ({ from, to, color: 'rgba(255, 255, 0, 0.7)' })),
          ],
          colorPlate: getCSSValue('--iq-darken'),
          colorMajorTicks: getCSSValue('--iq-color-secondary'),
          colorMinorTicks: getCSSValue('--iq-divider'),
          colorTitle: getCSSValue('--iq-color'),
          colorUnits: getCSSValue('--iq-color'),
          colorNumbers: getCSSValue('--iq-color-secondary'),
          colorNeedle: getCSSValue('--iq-accent'),
          colorNeedleEnd: getCSSValue('--iq-accent-secondary'),
          colorBarProgress: getCSSValue('--iq-accent'),
          colorBar: getCSSValue('--iq-divider'),
          borderShadowWidth: 0,
          borders: true,
          barWidth: 10,
          barBeginCircle: false,
          needleType: "line",
          needleWidth: 4,
          animationDuration: 1500,
          animationRule: "linear",
          value: sensor.state?.value || 0,
          valueBox: true,
          fontValueSize: 19,
          valueTextShadow: false,
        })

        gaugeRefs.current[index].draw()
      }
    })

    return () => {
      gaugeRefs.current.forEach(gauge => gauge && gauge.destroy())
      gaugeRefs.current = []
    }
  }, [summary, width, temperatureSensors, gaugeRefs])

  useEffect(() => {
    function check() {
      if (canvasRefs.current[0] && canvasRefs.current[0].parentNode) {
        const w = canvasRefs.current[0].parentNode.offsetWidth * 0.6
        setWidth(w)
      }
    }

    check()
    window.addEventListener('resize', check)
    return () => {
      window.removeEventListener('resize', check)
    }
  }, [temperatureSensors, canvasRefs])

  return <div className={ cn('gas-and-oil-summary-card', className) } { ...props }>
    <div className="content">
      { temperatureSensors.map((sensor, index) => {
        return <div key={ sensor.id || index } className="sensor-card">
          <div className="canvas-div">
            <canvas ref={ el => canvasRefs.current[index] = el }/>
          </div>
          <ProductionDiffLine
            center
            title={ t('averageTemperature.daily') }
            current={ pathOr(0, [scope, 'summary', 'dailyValue'], summary) }
            past={ pathOr(0, [scope, 'summary', 'pastDayValue'], summary) }
            unit={ UNIT_TEMPERATURE }
          />
          <ProductionDiffLine
            center
            title={ t('averageTemperature.monthly') }
            current={ pathOr(0, [scope, 'summary', 'monthlyValue'], summary) }
            past={ pathOr(0, [scope, 'summary', 'pastMonthValue'], summary) }
            unit={ UNIT_TEMPERATURE }
          />
          <ProductionDiffLine
            center
            title={ t('averageTemperature.yearly') }
            current={ pathOr(0, [scope, 'summary', 'yearlyValue'], summary) }
            past={ pathOr(0, [scope, 'summary', 'pastYearValue'], summary) }
            unit={ UNIT_TEMPERATURE }
          />

          <Status
            style={ { marginTop: '0.8rem' } }
            inline={ sensor.status !== 'NO_SIGNAL' }
            weak
            warning={ sensor.status === 'TECH_WORKS' }
            error={ sensor.status === 'NO_SIGNAL' || sensor.status === 'SHORT_CIRCUIT' || sensor.status === 'NO_SENSOR_SIGNAL' || sensor.status === 'UNKNOWN_UNIT' || sensor.status === 'INVALID_DRIVER' }
          >
            { t(`status.sensor.${ sensor.status }`) }
          </Status>

          <Button
            onClick={ () => {
              setCurrentSensor(sensor)
              setIsOpened(true)
            } }
            className={ 'sensor-visibility' }
            icon={ 'visibility' }
          />

        </div>
      }) }

      <SensorInfoModal
        isOpened={ isOpened }
        title={ `Информация о датчике ${ title }` }
        onClose={ () => setIsOpened(false) }
        footerActions={ [{ key: 'cancel', title: t('sensorInfo.close'), danger: true }] }
        currentSensor={ currentSensor }
        valueRenderer={ (value) => (value ? `${ value } °C` : 'Нет данных') }
      />
    </div>
    <Footer>
      <div className="iq-spacer">
        <b>{ title }</b>
      </div>
    </Footer>
  </div>
}
